import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	input,
	OnInit,
	output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { assigneeList } from '@app/layout/state/layout.selectors';
import { FormFieldLabelComponent } from '@app/shared/ui-components/form-field-label/form-field-label.component';
import { Store } from '@ngrx/store';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { IAssigneeUser } from '@shared/interfaces/selectable-options.interfaces';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { AuSearchInputComponent } from '@app/shared/ui-components/au-search-input/au-search-input.component';

@Component({
	selector: 'entity-responsible',
	imports: [
		FormFieldLabelComponent,
		MatFormFieldModule,
		NgxTolgeeModule,
		ReactiveFormsModule,
		MatSelectModule,
		MatIconModule,
		MatInputModule,
		NgxMatSelectSearchModule,
		MatChipsModule,
		MatMenuModule,
		AuSearchInputComponent,
	],
	templateUrl: './entity-responsible.component.html',
	styleUrl: './entity-responsible.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityResponsibleComponent implements OnInit {
	private readonly store$ = inject(Store);
	private readonly destroyRef = inject(DestroyRef);
	responsibleType = input<'assignee' | 'responsible'>('assignee');
	showAsChip = input<boolean>(false);
	selectedResponsible = input<IAssigneeUser>();
	responsibleChanged = output<IAssigneeUser>();

	compareWith = (o1: IAssigneeUser, o2: IAssigneeUser) => o1?.psId === o2?.psId;

	assigneeList = this.store$.selectSignal(assigneeList);

	allAssignees = computed(() =>
		(this.assigneeList() || []).reduce<IAssigneeUser[]>((accumulator, org) => {
			return [...accumulator, ...(org.users || [])];
		}, [])
	);

	selectableAssignees = computed(() => {
		const assignees = this.allAssignees();
		const search = (this.search() || '').toLowerCase().trim();

		if (!search) {
			return assignees;
		}

		return assignees.filter(assignee => {
			const firstName = assignee.firstName?.toLowerCase() || '';
			const lastName = assignee.lastName?.toLowerCase() || '';
			const fullName = `${firstName} ${lastName}`;

			return (
				firstName.includes(search) ||
				lastName.includes(search) ||
				fullName.includes(search)
			);
		});
	});

	responsible = new FormControl<IAssigneeUser>(null);
	responsibleSearch = new FormControl<string>('');
	search = toSignal(this.responsibleSearch.valueChanges);

	ngOnInit() {
		this.responsible.setValue(this.selectedResponsible());

		this.responsible.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value: IAssigneeUser) => {
				this.responsibleChanged.emit(value);
			});
	}
}
